import {
  lazy,
  MutableRefObject,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import dayjs from 'dayjs';
// import RightSidePanel from '../../SharedComponets/RightSidePanel';
import {
  Button,
  Input,
  TextArea,
  RadioBox,
  Select,
  CheckBox,
  Icon
} from '../../Universal/NovusDSImports';
import {
  btnStyles,
  checkBoxStyles,
  inputStyles,
  modalStyles,
  radioBoxStyles,
  selectStyles
} from '../../Universal/NovusDSImports/variants';
import { FormSegment, HeadingError } from '../globalStyles';
import { useReduxDispatch, useReduxSelector } from '../../Store/reduxHooks';
import {
  addClosure,
  submitted,
  submittingClosure
} from '../../Store/reducers/Closures';
import {
  getCentroidOfMultiplePolygons,
  isPolygonInvalid,
  removeEventManagedByLabel,
  safeDecodeJWTToken,
  showPopUp,
  validateSpanishMessageToggle,
  validateMaxVertices,
  validateYupSchema,
  differencefromCurrentTime,
  isPolygonsOverLapping,
  createWeaTextMessageMap
} from '../../CommonUtilities/CommonUtilities';
import Cookies from 'js-cookie';
import { STRINGS } from '../../Constants/ConstantStrings';
import html2canvas from 'html2canvas';
import { RootState } from '../../store';
import {
  clearUIforScreenshot,
  restoreUIforScreenshot
} from '../../CommonUtilities/screenshotUtlity';
import { BASE_URL } from '../../settings';
import WEAMessageTextArea from './WEAMessageTextArea';
import PALoader from '../../SharedComponets/PALoader';
import { CustomTextArea, StatusTooltip } from './styles';
import { TooltipComponent } from '../../SharedComponets/Tooltip';
import { colorState } from '../../Universal/Foundation/colors';
import { maxNumberOfPolygonsInClosure } from '../../Constants/constants';
import ExtendEventDurationConfirmationModal from './ExtendEventDurationConfirmationModal';
import ConfirmationPopupModal from '../../SharedComponets/ConfirmationPopupModal';

const RightSidePanel = lazy(
  () => import('../../SharedComponets/RightSidePanel')
);

interface AddEditClosureProps {
  drawnPolygon: any;
  isEdit: any;
  setDrawnPolygon: any;
  setIsDrawing: any;
  onClose: any;
  mapRef: MutableRefObject<google.maps.Map | null>;
  setIsDrawMode: (isDrawMode: boolean) => void;
  affectedAreaType: any;
  setAffectedAreaType: (areaType: string) => void;
  handlePolygonRefClear: () => void;
  setMapDefaultvalues: () => void;
}
const eventIDTooltipMessage = [
  {
    title: 'Restricted Characters',
    message: `The Restricted characters for the field Event ID are vertical bar (|), backslash (\\), forward slash (/), caret (^), tilde (~), typographic apostrophes (’), single quote ('), double quote ("), left parenthesis ((), right parenthesis ()), left curly brace ({), right curly brace (}), left square bracket ([), right square bracket (]), comma (,), space, greater than (>), less than (<), and ampersand (&).`
  }
];

const statusTooltipMessage = [
  {
    title: 'Two-Way Communication (Open)',
    message: `Signals two-way communication readiness. Choosing this status adds closure to the website, enables SMS and IVR, and initiates emergency alert approval (if "Send IPAWS Alert" is chosen).`
  },
  {
    title: 'Draft',
    message: `Saves event data without public distribution approval. Useful for delaying activation, editing closure questions, or for supervisor review.`
  },
  {
    title: 'One-way Communication (Preemptive)',
    message: `Linked to one-way emergency messages. Selecting this status removes the URL from the WEA alert and does not activate updates for the public website, SMS, or IVR. Triggers approval if "Send IPAWS Alert" is enabled.`
  }
];

const weaMessageAppendingUrl = 'Go to ' + BASE_URL.replace('admin-', '') + '/';

export default function AddEditClosure(props: AddEditClosureProps) {
  const {
    drawnPolygon,
    isEdit,
    setDrawnPolygon,
    setIsDrawing,
    onClose,
    mapRef,
    setIsDrawMode,
    affectedAreaType,
    setAffectedAreaType,
    handlePolygonRefClear,
    setMapDefaultvalues
  } = props;

  const selectedClosure = useReduxSelector(
      (state: RootState) => state.Common.selectedClosure
    ),
    [maintainedBy, setMaintainedBy] = useState<number | null>(
      isEdit ? selectedClosure?.maintained_by : null
    ),
    [isSaveForFuture, setIsSaveForFuture] = useState<boolean>(
      isEdit
        ? selectedClosure?.save_for_future
          ? selectedClosure?.save_for_future
          : false
        : false
    ),
    [isSendAlert, setIsSendAlert] = useState<boolean>(
      isEdit
        ? selectedClosure?.alert_status !== 'not_submitted'
          ? true
          : false
        : false
    ),
    [isMaintaniedBySelected, setIsMaintainedBySelected] =
      useState<boolean>(true),
    [isAffectedAreaTypeSelected, setIsAffectedAreaTypeSelected] =
      useState<boolean>(true),
    [showIPAWSAlert, setShowIPAWSAlert] = useState<boolean>(false),
    [isEditingState, setIsEditingState] = useState<boolean>(isEdit),
    [weaMessageAppendingUrlLength, setWeaMessageAppendingUrlLength] =
      useState<number>(0),
    [isConfirmationModalOpen, setIsConfirmationModalOpen] =
      useState<boolean>(false),
    [temporaryAffectedAreaType, setTemporaryAffectedAreaType] = useState<any>(),
    dispatch = useReduxDispatch(),
    ticketStatuses = useReduxSelector(
      (state: RootState) => state.Closures.ticketStatuses
    ),
    eventNames = useReduxSelector(
      (state: RootState) => state.Closures.eventNames
    ),
    savedPolygons = useReduxSelector(
      (state: RootState) => state.Closures.savedPolygons
    ),
    eventMaintainers = useReduxSelector(
      (state: RootState) => state.Closures.eventMaintainers
    ),
    isAddClosureSuccess = useReduxSelector(
      (state: RootState) => state.Closures.isAddClosureSuccess
    ),
    isSubmitting = useReduxSelector(
      (state: RootState) => state.Closures.isSubmitting
    ),
    [toggleSpanishTextBox, setToggleSpanishTextBox] = useState<boolean>(false);
  const [
    isExtendEventDurationConfirmationModalOpen,
    setIsExtendEventDurationConfirmationModalOpen
  ] = useState<boolean>(false);

  const weaTextMessageMap = useMemo(() => {
    return createWeaTextMessageMap(eventMaintainers);
  }, [eventMaintainers]);
  const [status, setStatus] = useState<any>(null);
  const [eventOptions, setEventOptions] = useState<any>(null);
  const [alertDurationOptions, setAlertDurationOptions] = useState<any>(null);
  const [extendEventDurationOptions, setExtendEventDurationOptions] =
    useState<any>(null);
  const [reduceEventDurationOptions, setReduceEventDurationOptions] =
    useState<any>(null);

  useEffect(() => {
    if (isSendAlert) {
      const options = hourOptions(!!selectedClosure?.alert_expiry);
      setAlertDurationOptions(options);

      if (isEdit && selectedClosure?.alert_status === 'approved') {
        const extendOptions = updateDurationOptions(
          selectedClosure?.alert_duration,
          'extend'
        );
        setExtendEventDurationOptions(extendOptions);

        const reduceOptions = updateDurationOptions(
          selectedClosure?.alert_duration,
          'reduce'
        );
        setReduceEventDurationOptions(reduceOptions);
      }
    }
  }, [isSendAlert, selectedClosure?.id]);

  useEffect(() => {
    if (eventNames) {
      const options = eventTypeOptions();

      setEventOptions(options);
    }
  }, [eventNames]);

  useEffect(() => {
    if (ticketStatuses) {
      const options = statusOptions();

      setStatus(options);
    }
  }, [ticketStatuses]);

  const savedPolygonOptions = useMemo(() => {
    const savedPolygonsOption: any = [];
    savedPolygons?.forEach((polygon: any) => {
      if (
        !(
          selectedClosure?.new_polygon &&
          selectedClosure?.saved_polygon?.some(
            (data) => data?.id === polygon.id
          )
        )
      ) {
        savedPolygonsOption.push({
          label: polygon.polygon_name,
          value: polygon.id
        });
      }
    });
    return savedPolygonsOption;
  }, [savedPolygons]);

  const statusOptions = useCallback(() => {
    const statusOptions: any = [];
    ticketStatuses?.forEach((status: any) => {
      if (status.name !== 'Closed') {
        statusOptions.push({ label: status.name, value: status.id });
      }
    });
    return statusOptions;
  }, [ticketStatuses, isEdit, selectedClosure]);

  const eventTypeOptions = useCallback(() => {
    const eventOptions: any = [];
    eventNames?.forEach((status: any) => {
      eventOptions.push({ label: status.name, value: status.id });
    });
    return eventOptions;
  }, [eventNames]);

  const hourOptions = (extendhrsFormat = false) => {
    let maxValue = 24;
    const hourOptions: any = [];

    if (extendhrsFormat) {
      maxValue = 96;
    }
    for (let i = 1; i <= maxValue; i++) {
      hourOptions.push({ label: i, value: i });
    }
    return hourOptions;
  };

  const [hoursLeft, setHoursLeft] = useState<number>(0);
  const [secondsLeft, setSecondsLeft] = useState<number>(0);

  // Function for options in extend/reduce event duration
  const updateDurationOptions = useCallback(
    (alertDuration: number, type: 'extend' | 'reduce') => {
      const options: Array<{ label: any; value: any }> = [];

      const diffInSeconds = differencefromCurrentTime(
        selectedClosure?.alert_expiry
      );

      const diffInHours = Math.floor(diffInSeconds / 3600);

      setHoursLeft(diffInHours);
      setSecondsLeft(diffInSeconds);

      let maxOptionsValue = diffInHours;
      if (type === 'extend') {
        // Checking how many hours are left from 96 hours (as 96 is the maximum allowed duration for a event)
        const hoursLeftFrom96 = 96 - alertDuration;

        // Checking how many hours we can actually extend the event duration according to the countdown timer
        const hoursDiffFromCountdown = 24 - diffInHours;

        // If the hours left from 96 is less than the hours left from countdown,
        // then we can only extend the event duration for that much time
        // Else we can extend it for the hours left from countdown
        if (hoursLeftFrom96 <= hoursDiffFromCountdown) {
          maxOptionsValue = hoursLeftFrom96;
        } else {
          maxOptionsValue = hoursDiffFromCountdown - 1;
        }
      } else {
        maxOptionsValue = diffInHours;
      }

      // Creating options for the select dropdown
      for (let i = 0; i <= maxOptionsValue; i++) {
        options.push({ label: i, value: i });
      }
      return options;
    },
    [selectedClosure?.id]
  );

  const getStatus = useCallback(
    (id: number) => {
      let option: any = null;
      statusOptions()?.forEach((status: any) => {
        if (status.value === id) {
          option = status;
        }
      });
      return option;
    },
    [statusOptions]
  );

  const getEventType = useCallback(
    (id: number) => {
      let option: any = null;
      eventTypeOptions()?.forEach((event: any) => {
        if (event.value === id) {
          option = event;
        }
      });
      return option;
    },
    [eventTypeOptions]
  );

  // const getSavedPlygon = (id: number) => {
  //   let option: any = null;
  //   savedPolygonOptions() &&
  //     savedPolygonOptions().forEach((polygon: any) => {
  //       if (polygon.value === id) {
  //         option = polygon;
  //       }
  //     });
  //   return option;
  // };

  const getHour = useCallback((id: number) => {
    let option: any = null;
    hourOptions(!!selectedClosure?.alert_expiry)?.forEach((hour: any) => {
      if (hour.value === id) {
        option = hour;
      }
    });
    return option;
  }, []);

  const getDefaultValues = useCallback(() => {
    const dict: any = {
      eventId: isEdit
        ? selectedClosure?.event_id
        : `${dayjs().format('YYYYMMDDHHmm')}`,
      shortMessage: isEdit ? selectedClosure?.wea_short_msg_english : '',
      longMessage: isEdit ? selectedClosure?.wea_long_msg_english : '',
      spanishShortMessage: isEdit
        ? selectedClosure?.wea_short_msg_spanish ||
          weaTextMessageMap[maintainedBy ?? 'null']
        : '',
      spanishLongMessage: isEdit
        ? selectedClosure?.wea_long_msg_spanish ||
          weaTextMessageMap[maintainedBy ?? 'null']
        : '',
      description: isEdit ? selectedClosure?.description : '',
      centerLat: isEdit
        ? `${selectedClosure?.centre_latitude}`
        : drawnPolygon
          ? getCentroidOfMultiplePolygons(drawnPolygon)[1]
          : '',
      centerLong: isEdit
        ? selectedClosure?.centre_longitude
        : drawnPolygon
          ? getCentroidOfMultiplePolygons(drawnPolygon)[0]
          : '',
      affectedArea:
        isEdit &&
        !selectedClosure?.new_polygon &&
        selectedClosure?.saved_polygon
          ? selectedClosure?.saved_polygon.map((polygon) => ({
            label: polygon.polygon_name,
            value: polygon.id
          }))
          : [],
      status: isEdit ? getStatus(selectedClosure?.status?.id) : null,
      eventType: isEdit ? getEventType(selectedClosure?.event_type) : null,
      alertDuration: isEdit ? getHour(selectedClosure?.alert_duration) : null,
      extendEventDuration: selectedClosure?.alert_status === 'approved' && {
        label: '0',
        value: 0
      },
      reduceEventDuration: selectedClosure?.alert_status === 'approved' && {
        label: '0',
        value: 0
      },
      polygonName: ''
    };
    return dict;
  }, [
    drawnPolygon,
    getEventType,
    getHour,
    getStatus,
    isEdit,
    selectedClosure,
    ticketStatuses
  ]);

  const schema = yup.object().shape({
    eventId: validateYupSchema('eventId', STRINGS.YUP.EVENT_ID, true),
    status: yup.object().required('Please select a status'),
    shortMessage: validateYupSchema(
      'weaMessageEnglish',
      STRINGS.YUP.SHORT_MESSAGE,
      true,
      {
        maxNumber: 90 - weaMessageAppendingUrlLength,
        eventMaintainers: eventMaintainers
      }
    ),
    longMessage: validateYupSchema(
      'weaMessageEnglish',
      STRINGS.YUP.LONG_MESSAGE,
      true,
      {
        maxNumber: 360 - weaMessageAppendingUrlLength,
        eventMaintainers: eventMaintainers
      }
    ),
    spanishShortMessage: validateYupSchema(
      'weaMessage',
      STRINGS.YUP.SPANISH_SHORT_MESSAGE,
      false,
      {
        maxNumber: 90 - weaMessageAppendingUrlLength,
        eventMaintainers: eventMaintainers
      }
    ),
    spanishLongMessage: validateYupSchema(
      'weaMessage',
      STRINGS.YUP.SPANISH_LONG_MESSAGE,
      false,
      {
        maxNumber: 360 - weaMessageAppendingUrlLength,
        eventMaintainers: eventMaintainers
      }
    ),
    description: validateYupSchema('string', 'Description', true),
    affectedArea: validateYupSchema(
      'multiSelect',
      STRINGS.YUP.AFFECTED_AREA,
      false
    ),
    centerLat: validateYupSchema('string', STRINGS.YUP.CENTER_LATITUDE, true),
    centerLong: validateYupSchema('string', STRINGS.YUP.CENTER_LONGITUDE, true),
    polygonName: validateYupSchema(
      'string',
      STRINGS.YUP.POLYGON_NAME,
      isSaveForFuture
    ),
    eventType: validateYupSchema(
      'select',
      STRINGS.YUP.EVENT_CATEGORY,
      isSendAlert
    ),
    alertDuration: validateYupSchema(
      'select',
      STRINGS.YUP.ALERT_DURATION,
      isSendAlert
    )
  });

  const methods = useForm({
    defaultValues: getDefaultValues(),
    resolver: yupResolver(schema)
  });

  const {
    handleSubmit,
    control,
    watch,
    reset,
    getValues,
    clearErrors,
    setValue,
    trigger,
    formState: { errors, isSubmitted }
  } = methods;

  useEffect(() => {
    if (selectedClosure) {
      reset(getDefaultValues());
      setDrawnPolygon(selectedClosure?.geometry?.coordinates[0]);
    }
    // getDefaultValues needs to be memoized or similar to fix this eslint
  }, [reset, selectedClosure, setDrawnPolygon, ticketStatuses]);

  useEffect(() => {
    if (isEdit && selectedClosure?.alert_status !== 'not_submitted') {
      setIsSendAlert(true);
    } else {
      setIsSendAlert(false);
    }
    if (isEdit && selectedClosure) {
      setAffectedAreaType(
        !selectedClosure?.new_polygon && selectedClosure?.saved_polygon?.length
          ? 'select_polygon'
          : 'draw_polygon'
      );
    } else {
      setAffectedAreaType('');
    }
  }, [isEdit, selectedClosure]);

  const watchWEAShortMessage = watch('shortMessage'),
    watchWEALongMessage = watch('longMessage'),
    watchWEAShortMessageSpanish = watch('spanishShortMessage'),
    watchWEALongMessageSpanish = watch('spanishLongMessage'),
    watchAffectedArea = watch('affectedArea'),
    watchStatus = watch('status'),
    watchShortMessage = watch('shortMessage'),
    watchCenterLat = watch('centerLat'),
    watchCenterLong = watch('centerLong');

  useEffect(() => {
    if (
      isEdit &&
      (selectedClosure?.wea_short_msg_spanish ||
        selectedClosure?.wea_long_msg_spanish)
    ) {
      if (validateSpanishMessageToggle(selectedClosure)) {
        setToggleSpanishTextBox(true);
      } else {
        setToggleSpanishTextBox(false);
      }
    }
  }, [isEdit, selectedClosure]);

  useEffect(() => {
    const val = getValues('shortMessage');
    //using iseditingstate to stop the shortmessage getting assigned to long message when default value is assigned in edit mode
    if (val?.length >= 0 && !isEditingState) {
      setValue('longMessage', val);
      if (isSubmitted) {
        trigger('longMessage');
      }
    }
    //if spanish long message doesnot exists, then set the iseditingstate false here itself
    //if spanish long message exists, then iseditstate will be made false in succeeding useeffect (watchweashortmessagespanish)
    if (isEdit && !selectedClosure?.wea_long_msg_spanish) {
      setIsEditingState(false);
    }
  }, [getValues, setValue, watchShortMessage, selectedClosure]);

  useEffect(() => {
    const val = getValues('spanishShortMessage');

    if (val?.length >= 0 && !isEditingState) {
      setValue('spanishLongMessage', val);
      if (isSubmitted) {
        trigger('spanishLongMessage');
      }
    }
    setIsEditingState(false);
  }, [getValues, setValue, watchWEAShortMessageSpanish]);

  useEffect(() => {
    setShowIPAWSAlert(
      watchStatus && (watchStatus.value === 1 || watchStatus.value === 2)
    );
    setWeaMessageAppendingUrlLength(
      watchStatus?.label === 'Two-Way Communications (Open)'
        ? weaMessageAppendingUrl?.length + 1
        : 0
    );
  }, [watchStatus]);

  useEffect(() => {
    if (!(drawnPolygon?.length <= 1)) {
      setValue('polygonName', '');
      setIsSaveForFuture(false);
    }
  }, [drawnPolygon, affectedAreaType]);

  useEffect(() => {
    if (isSaveForFuture) {
      setValue('polygonName', '');
      const element = document.getElementById('polygonName');
      element?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [isSaveForFuture]);

  const stateBoundary = useReduxSelector(
    (state: RootState) => state.ClientInfo.state_boundary
  );

  const onSubmit = async (data: any) => {
    setIsAffectedAreaTypeSelected(affectedAreaType?.length ? true : false);
    setIsMaintainedBySelected(maintainedBy !== null);
    if (!drawnPolygon?.length) {
      showPopUp(STRINGS.PLEASE_DRAW_A_POLYGON, 'warning');
    } else if (
      drawnPolygon?.length > 1 &&
      isPolygonsOverLapping(drawnPolygon)
    ) {
      showPopUp(STRINGS.POLYGONS_OVERLAPPING, 'error');
    } else if (isAffectedAreaTypeSelected && maintainedBy !== null) {
      dispatch(submittingClosure());
      const componentRef = document.getElementById('map-wrapper');
      clearUIforScreenshot();
      if (componentRef) {
        // setTimeout is used to wait for the UI to be updated before taking the screenshot
        setTimeout(async () => {
          await html2canvas(componentRef, { useCORS: true })
            .then((canvas) => {
              const screenshot = canvas.toDataURL('image/png');
              return screenshot.split(',')[1];
            })
            .then((screenShot) => {
              if (stateBoundary?.coordinates) {
                drawnPolygon?.forEach((polygon: any) => {
                  const polygonInvalidError = isPolygonInvalid(
                    polygon,
                    true,
                    stateBoundary?.coordinates
                  );

                  if (polygonInvalidError) {
                    throw new Error(polygonInvalidError);
                  }
                });
                return screenShot;
              } else {
                throw new Error(STRINGS.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN);
              }
            })
            .then((screenShot) => {
              restoreUIforScreenshot();

              const json = JSON.parse(
                JSON.stringify(safeDecodeJWTToken(Cookies.get('access') || ''))
              );
              const payload = {
                centre_latitude: data.centerLat,
                wea_short_msg_english: data.shortMessage,
                wea_long_msg_english: data.longMessage,
                wea_short_msg_spanish: removeEventManagedByLabel(
                  data.spanishShortMessage,
                  eventMaintainers
                )?.length
                  ? data.spanishShortMessage
                  : null,
                wea_long_msg_spanish: removeEventManagedByLabel(
                  data.spanishLongMessage,
                  eventMaintainers
                )?.length
                  ? data.spanishLongMessage
                  : null,
                status_id: data.status.value,
                event_id: data.eventId,
                centre_longitude: data.centerLong,
                alert_status: isSendAlert ? 'pending_review' : 'not_submitted',
                save_for_future: isSaveForFuture,
                description: data.description,
                created_user: json.user_id,
                maintained_by: maintainedBy,
                closure_image: screenShot,
                saved_polygon_id:
                  data.affectedArea?.map((option) => option.value) || [],
                new_polygon: affectedAreaType === 'draw_polygon',
                geometry: {
                  type: 'MultiPolygon',
                  coordinates: [
                    drawnPolygon.map((polygon) =>
                      polygon.map((coords) => [coords[1], coords[0]])
                    )
                  ]
                }
              };
              if (isSaveForFuture) {
                payload['polygon_name'] = data.polygonName;
              }
              if (isSendAlert) {
                payload['event_type'] = data.eventType.value;
                payload['alert_duration'] = data.alertDuration.value;

                if (isEdit) {
                  payload['extend_alert_duration'] =
                    data.extendEventDuration?.value;

                  payload['reduce_alert_duration'] =
                    data.reduceEventDuration?.value;
                }
              } else {
                payload['event_type'] = null;
                payload['alert_duration'] = null;
              }
              if (isEdit) {
                payload['id'] = selectedClosure?.id;
                dispatch(addClosure({ isEdit: true, params: payload }));
              } else {
                dispatch(addClosure({ params: payload }));
              }
            })
            .catch((error) => {
              showPopUp(error, 'error');
              dispatch(submitted());
            });
        }, 10);
      }
    }
  };

  useEffect(() => {
    if (isAddClosureSuccess) {
      setDrawnPolygon(null);
      onClose();
      setMapDefaultvalues();
    }
  }, [isAddClosureSuccess, onClose]);

  useEffect(() => {
    if (drawnPolygon?.length) {
      setValue('centerLat', getCentroidOfMultiplePolygons(drawnPolygon)[1]);
      setValue('centerLong', getCentroidOfMultiplePolygons(drawnPolygon)[0]);
    } else {
      setValue('affectedArea', []);
      setValue('centerLat', '');
      setValue('centerLong', '');
    }
  }, [drawnPolygon, setValue]);

  const options = (eventMaintainers ?? []).map((item: any) => ({
    value: item.id,
    label: item.name,
    displayText: item.display_text
  }));

  const affectedAreaTypeOptions = [
    { label: STRINGS.DRAW_POLYGON_ON_MAP, value: 'draw_polygon' },
    { label: STRINGS.CHOOSE_FROM_SHAPES_LIBRARY, value: 'select_polygon' }
  ];

  const handleRemovingAffectedArea = useCallback(
    (removedOption: any, tempdata?: any, allClear?: boolean) => {
      const data = tempdata || [...drawnPolygon];
      //find the removed polygon based on affectedArea option
      let removedPolygon;
      savedPolygons.forEach((polygon) => {
        if (polygon.id === removedOption.value) {
          removedPolygon = polygon;
          return;
        }
      });
      let removedOptionIndex;

      data?.forEach((polygon, index) => {
        if (
          JSON.stringify(polygon) ===
          JSON.stringify(removedPolygon?.geometry.coordinates[0][0])
        ) {
          removedOptionIndex = index;
        }
      });
      // an affectedArea may contain more than pne polygons, so when an option is removed, all the polygons in that option must be removed
      if (removedOptionIndex !== undefined) {
        data.splice(
          removedOptionIndex,
          removedPolygon?.geometry.coordinates[0].length
        );
      }
      if (allClear) {
        return data;
      }
      setDrawnPolygon(data.length ? data : null);
      return;
    },
    [drawnPolygon]
  );

  const onAffectedAreaChange = (e: any) => {
    setIsDrawing(false);

    //if the affected area is empty, we will clear the drawn polygon and set the value to null
    //this will happen when the user unchecks all the options in the affected area using cross button in the dropdown right side
    if (!e?.length) {
      let data: any = [...drawnPolygon];
      getValues('affectedArea')?.forEach((option) => {
        data = handleRemovingAffectedArea(option, data, true);
      });
      setDrawnPolygon(data.length ? data : null);
    }
    //if any option is unchecked, we will find the removed option by comparing affectedArea value & event
    //remove the unchecked option polygons from drawnpolygon
    else if (getValues('affectedArea')?.length > e.length) {
      const removedOption = getValues('affectedArea').filter(
        (obj1) => !e.some((obj2) => obj1.value === obj2.value)
      );
      handleRemovingAffectedArea(removedOption?.[0]);
      setValue('affectedArea', e.length ? e : []);
    }
    //else add the polygons in option to drawnpolygon
    else {
      savedPolygons?.forEach((polygon: any) => {
        if (polygon.id === e?.[e.length - 1].value) {
          const multiPolygonArray = drawnPolygon
            ? [...drawnPolygon, ...polygon.geometry.coordinates[0]]
            : polygon.geometry.coordinates[0];
          //Maximum 5 polygons validation
          if (multiPolygonArray.length > maxNumberOfPolygonsInClosure) {
            showPopUp(
              'Maximum of 5 polygons can be added to a closure',
              'error'
            );
            return;
          }
          //maximum vertices validation
          else if (validateMaxVertices(multiPolygonArray)) {
            showPopUp(STRINGS.MAX_VERTICES_VALIDATION, 'error');
            return;
          }
          setValue('affectedArea', e);
          setDrawnPolygon(multiPolygonArray);
          setValue(
            'centerLat',
            getCentroidOfMultiplePolygons(multiPolygonArray)[1]
          );
          setValue(
            'centerLong',
            getCentroidOfMultiplePolygons(multiPolygonArray)[0]
          );
          const bounds = new google.maps.LatLngBounds(null);
          polygon.geometry.coordinates[0][0]?.forEach((point) => {
            bounds.extend({ lat: point[0], lng: point[1] });
          });
          mapRef.current?.fitBounds(bounds);
        }
      });
    }
  };

  const onInValid = () => {
    setIsAffectedAreaTypeSelected(affectedAreaType?.length ? true : false);
    setIsMaintainedBySelected(maintainedBy !== null);
    if (!drawnPolygon?.length) {
      showPopUp(STRINGS.PLEASE_DRAW_A_POLYGON, 'warning');
    }
  };

  useEffect(() => {
    if (affectedAreaType === 'select_polygon') {
      const data: any = [];
      savedPolygons?.forEach((saved_polygon) => {
        if (
          getValues('affectedArea')?.some(
            (option) => option.value === saved_polygon.id
          )
        ) {
          if (
            drawnPolygon?.some(
              (polygon) =>
                JSON.stringify(polygon) ===
                JSON.stringify(saved_polygon.geometry.coordinates[0][0])
            )
          ) {
            data.push({
              label: saved_polygon.polygon_name,
              value: saved_polygon.id
            });
          }
        }
      });
      setValue('affectedArea', data);
    }
  }, [drawnPolygon]);

  useEffect(() => {
    const element =
      maintainedBy === null
        ? document.getElementById('eventManagedBy')
        : Object.keys(errors)[0] !== 'status' &&
          Object.keys(errors)[0] !== 'shortMessage' &&
          Object.keys(errors)[0] !== 'longMessage' &&
          !isAffectedAreaTypeSelected
          ? document.getElementById('affectedAreaType')
          : document.getElementById(Object.keys(errors)[0]);
    element?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  }, [errors]);

  useEffect(() => {
    if (watchAffectedArea) {
      setIsSaveForFuture(false);
    }
  }, [watchAffectedArea]);

  useEffect(() => {
    setValue('extendEventDuration', { value: 0, label: '0' });
    setValue('reduceEventDuration', { value: 0, label: '0' });
  }, [selectedClosure]);

  const ExtendEventDurationModalClose = () => {
    setValue('extendEventDuration', { value: 0, label: '0' });
    setValue('reduceEventDuration', { value: 0, label: '0' });
    setIsExtendEventDurationConfirmationModalOpen(false);
  };

  const watchExtendedEventDuration = watch('extendEventDuration')?.value;
  const watchReducedEventDuration = watch('reduceEventDuration')?.value;

  const handleConfirmationModalClose = () => {
    setIsConfirmationModalOpen(false);
    setTemporaryAffectedAreaType(null);
  };

  const handleAffectedAreaTypeChange = useCallback(
    (option?: any) => {
      handlePolygonRefClear();
      setAffectedAreaType(option?.value || temporaryAffectedAreaType?.value);
      setIsAffectedAreaTypeSelected(true);
      setIsDrawMode(
        option?.value === 'draw_polygon' ||
          temporaryAffectedAreaType?.value === 'draw_polygon'
          ? true
          : false
      );
      setIsDrawing(
        option?.value === 'draw_polygon' ||
          temporaryAffectedAreaType?.value === 'draw_polygon'
          ? true
          : false
      );
      setDrawnPolygon(null);
      setValue('affectedArea', []);
      handleConfirmationModalClose();
    },
    [
      temporaryAffectedAreaType,
      setDrawnPolygon,
      setValue,
      setIsDrawMode,
      setIsDrawing,
      handlePolygonRefClear,
      setAffectedAreaType,
      setIsAffectedAreaTypeSelected
    ]
  );

  const renderConfirmationFooter = () => {
    return (
      <div className="gap-3 d-flex">
        <Button
          onClick={() => handleConfirmationModalClose()}
          data-testid="switching-cancel-button"
          {...btnStyles.default}
        >
          {STRINGS.CANCEL}
        </Button>
        <Button
          data-testid="switching-confirm-button"
          onClick={handleAffectedAreaTypeChange}
          {...btnStyles.danger}
        >
          {STRINGS.YES}
        </Button>
      </div>
    );
  };

  useEffect(() => {
    if (watchCenterLat && watchCenterLong) {
      clearErrors(['centerLat', 'centerLong']);
    }
  }, [watchCenterLat, watchCenterLong]);

  const SectionBody = () => {
    return (
      <FormProvider {...methods}>
        <FormSegment
          onSubmit={(e) => {
            e.preventDefault();
            return watchExtendedEventDuration || watchReducedEventDuration
              ? (() => {
                setIsExtendEventDurationConfirmationModalOpen(true);
              })()
              : handleSubmit(onSubmit, onInValid)();
          }}
          onKeyDown={(e) => {
            if (
              e.key === 'Enter' &&
              (e.target as HTMLElement).tagName !== 'BUTTON'
            ) {
              e.preventDefault();
            }
          }}
        >
          <section className="formset-fields">
            {Object.keys(errors).length > 0 && (
              <HeadingError>
                {STRINGS.FILL_OUT_ALL_REQUIRED_FIELDS}
              </HeadingError>
            )}
            <div className="form-group" data-testid="event-id">
              <StatusTooltip>
                <div className="form-label">
                  {STRINGS.EVENT_ID} <span className="asterik">*</span>
                </div>

                <Icon
                  data-testid="event-id-info"
                  icon="info"
                  height="18px"
                  width="18px"
                  data-tooltip-id="event-id-tooltip"
                  aria-label="event id restricted characters"
                  aria-describedby="event-id-tooltip-content"
                />
                <p className="visually-hidden">
                  <div id="event-id-tooltip-content">
                    {eventIDTooltipMessage.map((item, index) => (
                      <div key={index} className="content">
                        <span className="content-heading" role="heading">
                          {item.title}:{' '}
                        </span>
                        <span className="content-description">
                          {item.message}
                        </span>
                      </div>
                    ))}
                  </div>
                </p>
                <TooltipComponent
                  id="event-id-tooltip"
                  place="bottom-end"
                  tooltipBgColor="#000"
                  tooltipTextColor="#fff"
                  tooltipMaxWidth="400px"
                >
                  <div className="tooltip-content" role="tooltip">
                    {eventIDTooltipMessage.map((item, index) => (
                      <div key={index} className="content">
                        <span className="content-heading" role="heading">
                          {item.title}:{' '}
                        </span>
                        <span className="content-description">
                          {item.message}
                        </span>
                      </div>
                    ))}
                  </div>
                </TooltipComponent>
              </StatusTooltip>
              <Controller
                name="eventId"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="eventId"
                    data-testid="event-id-input"
                    hideLabel={true}
                    disabled={isEdit}
                    type="text"
                    placeholder={STRINGS.ENTER_EVENT_ID}
                    isMargin={false}
                    ariaLabel="event id"
                    aria-description="The unique identifier for the event which is auto generated while creating a new closure. You can also edit this field before saving the closure."
                    {...inputStyles}
                    error={errors?.eventId}
                    hintText={errors?.eventId?.message}
                  />
                )}
              />
            </div>
            <div className="custom-radio-group" data-testid="event-managed-by">
              <div className="form-label">
                {STRINGS.EVENT_MANAGED_BY} <span className="asterik">*</span>
              </div>
              <div
                className="form-group"
                data-testid="event-managed-checkboxes"
              >
                {options?.map((option: any, index: number) => (
                  <RadioBox
                    id={`eventManagedBy-${option.displayText}`}
                    data-testid={`event-managed-by-${option.value}`}
                    aria-label={`event-managed-by-${option.label}`}
                    role="radio"
                    key={index}
                    aria-required="true"
                    disabled={isEdit}
                    text={option.displayText}
                    checked={maintainedBy === option.value}
                    onChange={() => {
                      setIsMaintainedBySelected(true);
                      setMaintainedBy(option.value);
                    }}
                    {...radioBoxStyles}
                  />
                ))}
              </div>
              {!isMaintaniedBySelected && (
                <p className="error-text">Please select one event manager</p>
              )}
            </div>

            <div className="form-group" data-testid="status">
              <StatusTooltip>
                <div className="form-label">
                  {STRINGS.STATUS} <span className="asterik">*</span>
                </div>

                <Icon
                  data-testid="status-info"
                  icon="info"
                  height="18px"
                  width="18px"
                  data-tooltip-id="status-tooltip"
                  aria-label="types of status"
                  aria-describedby="status-tooltip-content"
                />
                <p className="visually-hidden">
                  <div id="status-tooltip-content">
                    {statusTooltipMessage.map((item, index) => (
                      <div key={index} className="content">
                        <span className="content-heading" role="heading">
                          {item.title}:{' '}
                        </span>
                        <span className="content-description">
                          {item.message}
                        </span>
                      </div>
                    ))}
                  </div>
                </p>
                <TooltipComponent
                  id="status-tooltip"
                  place="bottom-end"
                  tooltipBgColor="#000"
                  tooltipTextColor="#fff"
                  tooltipMaxWidth="400px"
                >
                  <div className="tooltip-content" role="tooltip">
                    {statusTooltipMessage.map((item, index) => (
                      <div key={index} className="content">
                        <span className="content-heading" role="heading">
                          {item.title}:{' '}
                        </span>
                        <span className="content-description">
                          {item.message}
                        </span>
                      </div>
                    ))}
                  </div>
                </TooltipComponent>
              </StatusTooltip>
              <Controller
                name="status"
                control={control}
                render={({ field: { value, onChange, onBlur } }) => {
                  return (
                    <>
                      <Select
                        id="status"
                        data-testid="status-select"
                        ariaLabel="select status"
                        onBlur={onBlur}
                        value={value}
                        onChange={(e: any) => {
                          onChange(e);
                          if (!(e.value === 1 || e.value === 2)) {
                            setIsSendAlert(false);
                            setValue('eventType', null);
                            setValue('alertDuration', null);
                          }
                        }}
                        placeHolder={STRINGS.SELECT_OPTION}
                        options={status}
                        asterisk
                        {...selectStyles}
                        error={errors?.status}
                        hintText={errors?.status?.message}
                      />
                      {watchStatus?.label ===
                        'Two-Way Communications (Open)' && (
                        <p className="text-start mt-2">
                          {`
                      Note: '${weaMessageAppendingUrl}' will be appended to your WEA message title automatically.`}
                        </p>
                      )}
                    </>
                  );
                }}
              />
            </div>

            {showIPAWSAlert && (
              <div className="form-group" data-testid="send-ipaws-alert">
                <CheckBox
                  data-testid="send-ipaws-alert-checkbox"
                  onClick={() => {
                    if (
                      !isEdit ||
                      (isEdit &&
                        selectedClosure?.alert_status === 'not_submitted')
                    ) {
                      if (isSendAlert) {
                        setValue('eventType', null);
                        setValue('alertDuration', null);
                      }
                      setIsSendAlert(!isSendAlert);
                    }
                  }}
                  onKeyDown={(e: any) => {
                    if (e.key === ' ') {
                      if (
                        !isEdit ||
                        (isEdit &&
                          selectedClosure?.alert_status === 'not_submitted')
                      ) {
                        if (isSendAlert) {
                          setValue('eventType', null);
                          setValue('alertDuration', null);
                        }
                        setIsSendAlert(!isSendAlert);
                      }
                    }
                  }}
                  selected={isSendAlert}
                  disabled={
                    isEdit && selectedClosure?.alert_status !== 'not_submitted'
                  }
                  size={'md'}
                  text={
                    <p
                      data-testid="send-ipaws-alert-label"
                      onClick={() => {
                        if (
                          !isEdit ||
                          (isEdit &&
                            selectedClosure?.alert_status === 'not_submitted')
                        ) {
                          if (isSendAlert) {
                            setValue('eventType', null);
                            setValue('alertDuration', null);
                          }
                          setIsSendAlert(!isSendAlert);
                        }
                      }}
                      className="checkbox-label"
                    >
                      {STRINGS.SEND_IPAWS_ALERT}{' '}
                      <span>({STRINGS.REMEMBER_TO_CALL_PEMA})</span>
                    </p>
                  }
                  {...checkBoxStyles}
                />
              </div>
            )}

            {isSendAlert && (
              <div>
                <div className="form-group">
                  <Controller
                    name="eventType"
                    control={control}
                    render={({ field: { value, onChange } }) => {
                      return (
                        <Select
                          ariaLabel="select event category"
                          data-testid="event-category-select"
                          displayName={STRINGS.EVENT_CATEGORY}
                          value={value}
                          id="eventType"
                          onChange={onChange}
                          placeHolder={STRINGS.SELECT_OPTION}
                          options={eventOptions}
                          asterisk
                          {...selectStyles}
                          error={errors?.eventType}
                          hintText={errors?.eventType?.message}
                        />
                      );
                    }}
                  />
                </div>

                <div className="form-group">
                  <Controller
                    name="alertDuration"
                    control={control}
                    render={({ field: { value, onChange } }) => {
                      return (
                        <Select
                          ariaLabel="select alert duration"
                          value={value}
                          onChange={onChange}
                          data-testid="alert-duration-select"
                          id="alertDuration"
                          displayName={STRINGS.ALERT_DURATION_HOUR_S}
                          placeHolder={STRINGS.SELECT_OPTION}
                          options={alertDurationOptions}
                          disabled={isEdit && selectedClosure?.alert_expiry}
                          asterisk
                          {...selectStyles}
                          error={errors && errors.alertDuration}
                          hintText={errors?.alertDuration?.message}
                        />
                      );
                    }}
                  />
                </div>

                {isEdit && selectedClosure?.alert_status === 'approved' && (
                  <>
                    <div className="form-group">
                      <Controller
                        name="extendEventDuration"
                        control={control}
                        render={({ field: { value, onChange } }) => {
                          return (
                            <Select
                              ariaLabel="select extend event duration"
                              data-testid="extend-event-duration-select"
                              value={value}
                              onChange={(e) => {
                                onChange(e);
                                setValue('reduceEventDuration', {
                                  value: 0,
                                  label: '0'
                                });
                              }}
                              id="extendEventDuration"
                              displayName={'Extend Event Duration'}
                              placeHolder={STRINGS.SELECT_OPTION}
                              asterisk={false}
                              disabled={secondsLeft <= 0 || hoursLeft === 23}
                              options={extendEventDurationOptions}
                              {...selectStyles}
                            />
                          );
                        }}
                      />
                    </div>

                    <div className="form-group">
                      <Controller
                        name="reduceEventDuration"
                        data-testid="reduce-event-duration-select"
                        control={control}
                        render={({ field: { value, onChange } }) => {
                          return (
                            <Select
                              ariaLabel="select reduce event duration"
                              id="reduceEventDuration"
                              value={value}
                              onChange={(e) => {
                                onChange(e);
                                setValue('extendEventDuration', {
                                  value: 0,
                                  label: '0'
                                });
                              }}
                              placeHolder={STRINGS.SELECT_OPTION}
                              displayName={'Reduce Event Duration'}
                              disabled={hoursLeft <= 0}
                              options={reduceEventDurationOptions}
                              asterisk={false}
                              {...selectStyles}
                            />
                          );
                        }}
                      />
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="form-group">
              <Controller
                name="shortMessage"
                control={control}
                render={({ field }) => {
                  return (
                    <>
                      <WEAMessageTextArea
                        data-testid="wea-short-message-textarea"
                        {...field}
                        maxLength={90 - weaMessageAppendingUrlLength}
                        id="shortMessage"
                        aria-required="true"
                        label="511PA WEA Short Message"
                        placeholder="e.g. I-80 WB Closed from Exit xx (Town) to Exit xx (Town)"
                        hintText={errors?.shortMessage?.message || ''}
                        {...inputStyles}
                        asterisk
                        error={errors?.shortMessage}
                        maintainedBy={maintainedBy}
                      />

                      <p className="text-right">
                        {90 -
                          weaMessageAppendingUrlLength -
                          (watchWEAShortMessage?.length || 0)}{' '}
                        {STRINGS.CHARACTERS_REMAINING}
                      </p>
                    </>
                  );
                }}
              />
            </div>

            <div className="form-group">
              <Controller
                name="longMessage"
                control={control}
                render={({ field }) => {
                  return (
                    <>
                      <WEAMessageTextArea
                        data-testid="wea-long-message-textarea"
                        {...field}
                        maxLength={360 - weaMessageAppendingUrlLength}
                        id="longMessage"
                        label="511PA WEA Long Message"
                        aria-required="true"
                        placeholder="Write a Long Message"
                        hintText={errors?.longMessage?.message || ''}
                        error={errors?.longMessage}
                        {...inputStyles}
                        asterisk
                        maintainedBy={maintainedBy}
                      />
                      <p className="text-right">
                        {360 -
                          weaMessageAppendingUrlLength -
                          (watchWEALongMessage?.length || 0)}{' '}
                        {STRINGS.CHARACTERS_REMAINING}
                      </p>
                    </>
                  );
                }}
              />
            </div>

            {/* {!isEdit && ( */}
            <div className="form-group">
              <CheckBox
                id="spanish-textbox-toggle-checkbox"
                data-testid="spanish-textbox-toggle-checkbox"
                selected={toggleSpanishTextBox}
                onClick={() => setToggleSpanishTextBox((prev) => !prev)}
                text={
                  <p className="checkbox-label">Add Spanish Message Fields</p>
                }
                {...checkBoxStyles}
              />
            </div>
            {/* )} */}

            <div
              className={`form-group ${
                toggleSpanishTextBox ? '' : 'hide-field'
              }`}
            >
              <Controller
                name="spanishShortMessage"
                control={control}
                render={({ field }) => {
                  return (
                    <>
                      <CustomTextArea>
                        <label htmlFor="spanishShortMessage">
                          511PA WEA Short Message in Spanish{' '}
                          <span>(Non-Mandatory)</span>
                        </label>
                        <div className="custom-text-area">
                          <WEAMessageTextArea
                            data-testid="wea-spanish-short-message-textarea"
                            {...field}
                            maxLength={90 - weaMessageAppendingUrlLength}
                            id="spanishShortMessage"
                            placeholder="Write a Short Message"
                            hideLabel
                            hintText={
                              errors?.spanishShortMessage?.message || ''
                            }
                            error={errors?.spanishShortMessage}
                            nonMandatory={true}
                            maintainedBy={maintainedBy}
                            {...inputStyles}
                          />
                        </div>
                        {/* <p>
                      {`Note: ‘Ir a ${BASE_URL}/’
                      will be appended to your WEA message title automatically.`}
                    </p> */}
                        <p className="text-right">
                          {90 -
                            weaMessageAppendingUrlLength -
                            (watchWEAShortMessageSpanish?.length || 0)}{' '}
                          {STRINGS.CHARACTERS_REMAINING}
                        </p>
                      </CustomTextArea>
                    </>
                  );
                }}
              />
            </div>
            <div
              className={`form-group ${
                toggleSpanishTextBox ? '' : 'hide-field'
              }`}
            >
              <Controller
                name="spanishLongMessage"
                control={control}
                render={({ field }) => {
                  return (
                    <>
                      <CustomTextArea>
                        <label htmlFor="spanishLongMessage">
                          511PA WEA Long Message in Spanish{' '}
                          <span>(Non-Mandatory)</span>{' '}
                        </label>
                        <div className="custom-text-area">
                          <WEAMessageTextArea
                            data-testid="wea-spanish-long-message-textarea"
                            {...field}
                            maxLength={360 - weaMessageAppendingUrlLength}
                            id="spanishLongMessage"
                            placeholder="Write a Long Message"
                            nonMandatory={true}
                            hideLabel
                            hintText={errors?.spanishLongMessage?.message || ''}
                            error={errors?.spanishLongMessage}
                            maintainedBy={maintainedBy}
                            {...inputStyles}
                          />
                        </div>
                        {/* <p>
                      Note: ‘Ir a ${BASE_URL}/ will be appended to your WEA
                      message title automatically.
                    </p> */}
                        <p className="text-right">
                          {360 -
                            weaMessageAppendingUrlLength -
                            (watchWEALongMessageSpanish?.length || 0)}{' '}
                          {STRINGS.CHARACTERS_REMAINING}
                        </p>
                      </CustomTextArea>
                    </>
                  );
                }}
              />
            </div>

            <div className="form-group">
              <Controller
                control={control}
                name="description"
                render={({ field }) => {
                  return (
                    <TextArea
                      data-testid="description-textarea"
                      {...field}
                      id="description"
                      label={STRINGS.DESCRIPTION}
                      aria-required="true"
                      placeholder={STRINGS.ENTER_TEXT}
                      {...inputStyles}
                      asterisk
                      error={errors?.description}
                      hintText={errors?.description?.message}
                    />
                  );
                }}
              />
            </div>

            <div
              className="custom-radio-group"
              data-testid="affected-area-type"
            >
              <div className="form-label">
                {STRINGS.SELECT_AFFECTED_AREA}{' '}
                <span className="asterik">*</span>
              </div>
              <p className="polygon-area">{STRINGS.USERS_CAN_ADD}</p>
              <div className="form-group flex-column">
                {affectedAreaTypeOptions?.map((option: any, index: number) => (
                  <RadioBox
                    id={`affectedAreaType-${option.value}`}
                    aria-label={`select affected area type - ${option.label}`}
                    role="radio"
                    aria-required="true"
                    key={index}
                    data-testid={`affected-area-type-${option.value}`}
                    disabled={false}
                    text={option.label}
                    checked={affectedAreaType === option.value}
                    onChange={() => {
                      if (affectedAreaType?.length) {
                        setIsConfirmationModalOpen(true);
                        setTemporaryAffectedAreaType(option);
                      } else {
                        handleAffectedAreaTypeChange(option);
                      }
                    }}
                    onKeyDown={(e: any) => {
                      if (e.key === ' ' || e.key === 'Enter') {
                        if (affectedAreaType?.length) {
                          setIsConfirmationModalOpen(true);
                          setTemporaryAffectedAreaType(option);
                        } else {
                          handleAffectedAreaTypeChange(option);
                        }
                      }
                    }}
                    {...radioBoxStyles}
                  />
                ))}
              </div>
              {!isAffectedAreaTypeSelected && (
                <p className="error-text">Affected Area is required</p>
              )}
            </div>
            {affectedAreaType === 'select_polygon' && (
              <div className="form-group affected-area">
                <Controller
                  name="affectedArea"
                  control={control}
                  render={({ field: { value } }) => {
                    return (
                      <>
                        <Select
                          data-testid="affected-area-select"
                          isMultiSelect={true}
                          isMultiSelectWithTags={true}
                          limitTags={2}
                          isClearable={true}
                          showSelectAll={false}
                          withCheckbox={true}
                          value={value}
                          onChange={(e: any) => {
                            onAffectedAreaChange(e);
                          }}
                          id="affectedArea"
                          placeHolder={STRINGS.SELECT_OPTION}
                          isSearchable
                          options={savedPolygonOptions}
                          asterisk={true}
                          error={
                            !watch('affectedArea')?.length &&
                            errors?.affectedArea
                          }
                          hintText={
                            !watch('affectedArea')?.length &&
                            errors?.affectedArea?.message
                          }
                          {...selectStyles}
                          checkboxColor={colorState.icon.brand.primary}
                        />
                      </>
                    );
                  }}
                />
              </div>
            )}

            <div className="form-group">
              <Controller
                name="centerLat"
                control={control}
                render={({ field }) => {
                  return (
                    <Input
                      {...field}
                      id="centerLat"
                      data-testid="center-latitude-input"
                      label={STRINGS.CENTER_POINT_LATITUDE}
                      placeholder={STRINGS.ENTER_CENTER_POINT_LATITUDE}
                      isMargin={false}
                      aria-required="true"
                      {...inputStyles}
                      disabled
                      asterisk
                      error={!watch('centerLat') && errors?.centerLat}
                      hintText={
                        !watch('centerLat') && errors?.centerLat?.message
                      }
                    />
                  );
                }}
              />
            </div>

            <div className="form-group">
              <Controller
                name="centerLong"
                control={control}
                render={({ field }) => {
                  return (
                    <Input
                      {...field}
                      id="centerLong"
                      data-testid="center-longitude-input"
                      label={STRINGS.CENTER_POINT_LONGITUDE}
                      placeholder={STRINGS.ENTER_CENTER_POINT_LONGITUDE}
                      isMargin={false}
                      {...inputStyles}
                      disabled
                      asterisk
                      aria-required="true"
                      error={!watch('centerLong') && errors?.centerLong}
                      hintText={
                        !watch('centerLong') && errors?.centerLong?.message
                      }
                    />
                  );
                }}
              />
            </div>

            {affectedAreaType === 'draw_polygon' &&
              !watchAffectedArea?.length && (
              <div className="form-group">
                <CheckBox
                  id="save-alert-polygon-for-future-use-checkbox"
                  data-testid="save-alert-polygon-for-future-use-checkbox"
                  selected={isSaveForFuture}
                  onClick={() => {
                    setIsSaveForFuture(!isSaveForFuture);
                  }}
                  onKeyDown={(e: any) => {
                    if (e.key === ' ') {
                      setIsSaveForFuture(!isSaveForFuture);
                    }
                  }}
                  disabled={drawnPolygon?.length > 1}
                  size={'md'}
                  text={STRINGS.SAVE_ALERT_POLYGON_FOR_FUTURE_USE}
                  {...checkBoxStyles}
                />
              </div>
            )}

            {isSaveForFuture && (
              <div className="form-group">
                <Controller
                  name="polygonName"
                  control={control}
                  render={({ field }) => {
                    return (
                      <Input
                        data-testid="new-polygon-name-input"
                        {...field}
                        id="polygonName"
                        label={STRINGS.POLYGON_NAME}
                        placeholder={STRINGS.ENTER_POLYGON_NAME}
                        isMargin={false}
                        {...inputStyles}
                        asterisk
                        error={errors?.polygonName}
                        hintText={errors?.polygonName?.message}
                      />
                    );
                  }}
                />
              </div>
            )}
          </section>

          <section className="footer">
            <div className="d-flex gap-3 justify-content-end w-100">
              <Button
                {...btnStyles.secondary}
                data-testid="cancel-button"
                onClick={onClose}
                disabled={isSubmitting}
                aria-disabled={isSubmitting}
                type="button"
              >
                Cancel
              </Button>
              <Button
                {...btnStyles.primary}
                disabled={isSubmitting}
                aria-disabled={isSubmitting}
                data-testid="submit-button"
                type="submit"
              >
                {isEdit
                  ? isSubmitting
                    ? STRINGS.UPDATING
                    : STRINGS.UPDATE
                  : isSubmitting
                    ? STRINGS.SAVING
                    : STRINGS.SAVE}
              </Button>
            </div>
          </section>
        </FormSegment>
      </FormProvider>
    );
  };

  return (
    <>
      <Suspense fallback={<PALoader />}>
        <RightSidePanel
          header={`${isEdit ? 'Edit' : 'Add'} Closure`}
          body={SectionBody()}
          onClose={props.onClose}
        />
      </Suspense>
      <Suspense fallback={<PALoader />}>
        <ConfirmationPopupModal
          isOpen={isConfirmationModalOpen}
          showHeader={false}
          description={STRINGS.SWITCHING_BETWEEN_MODES_CONFIRMATION_TEXT}
          showFooter={renderConfirmationFooter()}
          postModalClose={() => handleConfirmationModalClose()}
          closeModal={() => handleConfirmationModalClose()}
          showBorderTop={false}
          isBodyPaddingNone={true}
          customFooter={true}
        />
      </Suspense>
      <ExtendEventDurationConfirmationModal
        isOpen={isExtendEventDurationConfirmationModalOpen}
        showHeader
        title="Event Duration"
        extendedDuration={
          selectedClosure?.alert_duration +
          (watchExtendedEventDuration
            ? watchExtendedEventDuration
            : -watchReducedEventDuration)
        }
        isExtend={!!watchExtendedEventDuration}
        submitForm={handleSubmit(onSubmit, onInValid)}
        closeModal={ExtendEventDurationModalClose}
        postModalClose={ExtendEventDurationModalClose}
        {...modalStyles}
      />
    </>
  );
}
